/**
 * The hand-offs of the day's steps (pedagogy spec §6.3.4), as plain URLs and session data so no
 * page imports another feature's internals:
 *
 * - cards → `/flashcards/review?start=mixed&limit=N&returnTo=…`; the review page fetches the
 *   mixed queue and its summary leads back to `/practice/today?done=cards`;
 * - re-tests → a mixed practice session of the returned problems, with their plan offers, each
 *   item carrying `origin: { kind: "today-retest", retestOf }`;
 * - the new topic → a topic session whose items carry `origin: { kind: "today-new" }`, with no
 *   plan step.
 *
 * Both practice sessions carry a `returnTo`, which gives the summary its "Fortsätt med dagens
 * pass" button. A Today step replaces an unfinished free-practice session, as `Page.tsx` does.
 */
import {
  createSession,
  isSessionComplete,
  loadSession,
  type PracticeSessionData,
} from "../session";
import type { AnswerOrigin } from "../types";
import { NEW_ITEMS, todayKey } from "./settings";
import { returnToFor } from "./session";
import type { StepId, TodayPlanDto } from "./types";

/** `/flashcards/review?start=mixed&limit=N&returnTo=%2Fpractice%2Ftoday%3Fdone%3Dcards`. */
export function cardsHref(limit: number): string {
  const search = new URLSearchParams({
    start: "mixed",
    limit: String(limit),
    returnTo: returnToFor("cards"),
  });
  return `/flashcards/review?${search.toString()}`;
}

/** The re-test step as a practice session, or null when the plan carries no problems for it. */
export function retestSession(plan: TodayPlanDto, limit: number): PracticeSessionData | null {
  const items = plan.retest.items.flatMap((item) =>
    item.question ? [{ item, question: item.question }] : [],
  );
  const chosen = items.slice(0, limit);
  if (chosen.length === 0) return null;
  const origins: Record<string, AnswerOrigin> = {};
  for (const { item, question } of chosen) {
    origins[question.id] = { kind: "today-retest", retestOf: item.retestOf };
  }
  return createSession(
    "mixed",
    chosen.map(({ question }) => question),
    plan.plans,
    { origins, returnTo: returnToFor("retest") },
  );
}

/** The new topic's first problems as a practice session, or null when there are none to serve. */
export function newTopicSession(plan: TodayPlanDto): PracticeSessionData | null {
  const questions = (plan.newTopic?.questions ?? []).slice(0, NEW_ITEMS);
  if (questions.length === 0) return null;
  const origins: Record<string, AnswerOrigin> = {};
  for (const question of questions) origins[question.id] = { kind: "today-new" };
  return createSession("topic", questions, undefined, { origins, returnTo: returnToFor("new") });
}

/**
 * The stored practice session when it is this step's own, unfinished and from today: "Fortsätt"
 * goes back into it instead of building a new one from a plan that has moved on.
 */
export function resumableSession(step: StepId, today: string): PracticeSessionData | null {
  const saved = loadSession();
  if (!saved || saved.returnTo !== returnToFor(step)) return null;
  if (isSessionComplete(saved) || todayKey(saved.startedAt) !== today) return null;
  return saved;
}
